import {
  clamp,
  esignFontSizePresets,
  fieldMinHeight,
  fieldMinWidth,
  fieldsOverlap,
  fieldTypeConfig,
  fieldTypeOrder,
  getFieldDisplayLabel,
  getFieldFontSize,
  getFieldPositionStyle,
  getFieldSampleValue,
  getFontSizeForHeight,
  getSignerTitle,
  isFieldRequiredMarkVisible,
  isFillableFieldType,
  round2,
  usesHandwritingFont,
} from "@/pages/esign/esign.config.ts";
import {
  ApiEsignMarkupField,
  ApiEsignMarkupRole,
  EsignFieldFilledBy,
  EsignFieldType,
} from "@/types/esign.types.ts";
import {
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
  Text,
} from "@skailer-com/uikit";
import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface EsignFieldBoxProps {
  field: ApiEsignMarkupField;
  roleIndex: number;
  baseWidth: number;
  baseHeight: number;
  boxClass: string;
  roles: ApiEsignMarkupRole[];
  layerRef: RefObject<HTMLDivElement | null>;
  onUpdate: (patch: Partial<ApiEsignMarkupField>) => void;
  onDelete: () => void;
  onChangeType: (fieldType: EsignFieldType) => void;
  onMoveToRole: (targetRoleIndex: number) => void;
}

export function EsignFieldBox({
  field,
  roleIndex,
  baseWidth,
  baseHeight,
  boxClass,
  roles,
  layerRef,
  onUpdate,
  onDelete,
  onChangeType,
  onMoveToRole,
}: EsignFieldBoxProps) {
  const { t } = useTranslation();

  const [isResizing, setIsResizing] = useState(false);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");

  const boxRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const movedRef = useRef(false);
  const editRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const isFillable = isFillableFieldType(field.field_type);
  const showRequiredMark = isFieldRequiredMarkVisible(field);

  const textWrapClass = field.is_multiline
    ? "max-h-full overflow-hidden whitespace-normal break-words text-center leading-tight"
    : "truncate leading-none";
  const textWrapStyle = { fontSize: getFieldFontSize(field) };

  const startLabelEdit = () => {
    setLabelDraft(field.label ?? "");
    setIsEditingLabel(true);
  };

  const commitLabel = () => {
    const trimmed = labelDraft.trim();
    onUpdate({ label: trimmed ? trimmed : null });
    setIsEditingLabel(false);
  };

  useEffect(() => {
    if (!isEditingLabel) return;
    const raf = window.requestAnimationFrame(() => {
      editRef.current?.focus();
      editRef.current?.select();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isEditingLabel]);

  const collidesWithOthers = (rect: {
    pos_x: number;
    pos_y: number;
    width: number;
    height: number;
  }) =>
    roles.some((role) =>
      role.fields.some(
        (other) => other !== field && other.page === field.page && fieldsOverlap(rect, other)
      )
    );

  useEffect(
    () => () => {
      dragCleanupRef.current?.();
    },
    []
  );

  useEffect(() => {
    if (!isResizing) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) {
        setIsResizing(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [isResizing]);

  const startPointerDrag = (
    event: ReactPointerEvent,
    onMoveDelta: (deltaX: number, deltaY: number) => void
  ) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    const layer = layerRef.current;
    if (!layer) return;
    dragCleanupRef.current?.();
    movedRef.current = false;

    const rect = layer.getBoundingClientRect();
    const pxToBaseX = baseWidth / rect.width;
    const pxToBaseY = baseHeight / rect.height;
    const startX = event.clientX;
    const startY = event.clientY;

    const onMove = (moveEvent: PointerEvent) => {
      if (Math.abs(moveEvent.clientX - startX) > 3 || Math.abs(moveEvent.clientY - startY) > 3) {
        movedRef.current = true;
      }
      onMoveDelta(
        (moveEvent.clientX - startX) * pxToBaseX,
        (moveEvent.clientY - startY) * pxToBaseY
      );
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = null;
    };
    const onUp = () => {
      cleanup();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = cleanup;
  };

  return (
    <div
      ref={boxRef}
      className={twMerge(
        "group absolute flex cursor-move items-center justify-center rounded",
        isResizing ? "border border-dashed" : "border-2",
        boxClass
      )}
      style={{ ...getFieldPositionStyle(field, baseWidth, baseHeight), containerType: "size" }}
      onDoubleClick={(event) => {
        if (!isFillable) return;
        event.stopPropagation();
        startLabelEdit();
      }}
      onPointerDown={(event) => {
        if (isEditingLabel) return;
        startPointerDrag(event, (deltaX, deltaY) => {
          const pos_x = round2(clamp(field.pos_x + deltaX, 0, baseWidth - field.width));
          const pos_y = round2(clamp(field.pos_y + deltaY, 0, baseHeight - field.height));
          if (collidesWithOthers({ pos_x, pos_y, width: field.width, height: field.height }))
            return;
          onUpdate({ pos_x, pos_y });
        });
      }}
    >
      {isEditingLabel ? (
        field.is_multiline ? (
          <textarea
            ref={editRef as RefObject<HTMLTextAreaElement>}
            value={labelDraft}
            className={"h-full w-full resize-none rounded bg-light px-1 leading-tight"}
            style={textWrapStyle}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setLabelDraft(event.target.value)}
            onBlur={commitLabel}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                setIsEditingLabel(false);
              }
            }}
          />
        ) : (
          <input
            ref={editRef as RefObject<HTMLInputElement>}
            value={labelDraft}
            className={`w-full rounded bg-light px-1 text-center leading-none ${
              field.field_type === EsignFieldType.text ? "" : "text-base"
            }`}
            style={field.field_type === EsignFieldType.text ? textWrapStyle : undefined}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setLabelDraft(event.target.value)}
            onBlur={commitLabel}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitLabel();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                setIsEditingLabel(false);
              }
            }}
          />
        )
      ) : isResizing ? (
        <span
          className={`pointer-events-none px-1 ${
            usesHandwritingFont(field.field_type) ? "font-signature " : ""
          }${textWrapClass}`}
          style={textWrapStyle}
        >
          {isFillable ? getFieldDisplayLabel(field, t) : getFieldSampleValue(field.field_type, t)}
          {showRequiredMark && <span className={"align-super"}>*</span>}
        </span>
      ) : isFillable ? (
        <span
          className={twMerge(
            "title-sub-headline cursor-text rounded px-1 hover:bg-primary/10",
            textWrapClass
          )}
          style={textWrapStyle}
          onClick={(event) => {
            event.stopPropagation();
            if (movedRef.current) return;
            startLabelEdit();
          }}
        >
          {getFieldDisplayLabel(field, t)}
          {showRequiredMark && <span className={"align-super"}>*</span>}
        </span>
      ) : (
        <Text
          className={`title-sub-headline pointer-events-none px-1 ${
            usesHandwritingFont(field.field_type) ? "font-signature " : ""
          }${textWrapClass}`}
          size="xs"
          style={textWrapStyle}
        >
          {getFieldDisplayLabel(field, t)}
          {showRequiredMark && <span className={"align-super"}>*</span>}
        </Text>
      )}

      <div
        className={
          "absolute right-0 top-0 flex items-center gap-1 opacity-0 group-hover:opacity-100"
        }
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
      >
        {isFillable && !isEditingLabel && (
          <ButtonIcon
            size={"sm"}
            variant={"secondary"}
            icon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
            onClick={(event) => {
              event.stopPropagation();
              startLabelEdit();
            }}
          />
        )}
        <DropdownMenu
          placement={"bottom-end"}
          trigger={
            <ButtonIcon
              size={"sm"}
              variant={"secondary"}
              icon={<FontAwesomeIcon icon={"fa-light fa-ellipsis"} />}
            />
          }
        >
          {!isFillable && (
            <DropdownMenuSub
              title={t("core:esign_change_signer")}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-user-pen"} />}
            >
              {roles.map((role, index) => (
                <DropdownMenuItem
                  key={`role-${index}`}
                  title={getSignerTitle(role, t)}
                  onClick={() => {
                    onMoveToRole(index);
                  }}
                />
              ))}
            </DropdownMenuSub>
          )}
          <DropdownMenuSub
            title={t("core:esign_change_type")}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-field"} />}
          >
            {fieldTypeOrder.map((type) => (
              <DropdownMenuItem
                key={type}
                title={t(fieldTypeConfig[type].labelKey)}
                isSelected={field.field_type === type}
                onClick={() => {
                  onChangeType(type);
                }}
              />
            ))}
          </DropdownMenuSub>
          <DropdownMenuSub
            title={t("core:esign_font_size")}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-text-size"} />}
          >
            {esignFontSizePresets.map((size) => (
              <DropdownMenuItem
                key={size}
                title={String(size)}
                isSelected={field.font_size === size}
                onClick={() => {
                  onUpdate({ font_size: size });
                }}
              />
            ))}
          </DropdownMenuSub>
          {isFillable && (
            <>
              <DropdownMenuSub
                title={t("core:esign_filled_by")}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
              >
                <DropdownMenuItem
                  title={t("core:esign_sender_fills_option")}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-paper-plane"} />}
                  isSelected={field.filled_by === EsignFieldFilledBy.sender}
                  onClick={() => {
                    onUpdate({ filled_by: EsignFieldFilledBy.sender });
                  }}
                />
                {roles.map((role, index) => (
                  <DropdownMenuItem
                    key={`signer-${index}`}
                    title={getSignerTitle(role, t)}
                    isSelected={
                      field.filled_by === EsignFieldFilledBy.signer && roleIndex === index
                    }
                    onClick={() => {
                      onUpdate({ filled_by: EsignFieldFilledBy.signer });
                      if (roleIndex !== index) onMoveToRole(index);
                    }}
                  />
                ))}
              </DropdownMenuSub>
              <DropdownMenuItem
                title={t("core:esign_field_required")}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-asterisk"} />}
                isSelected={field.is_required}
                onClick={() => {
                  onUpdate({ is_required: !field.is_required });
                }}
              />
              {field.field_type === EsignFieldType.text && (
                <DropdownMenuItem
                  title={t("core:esign_field_multiline")}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-bars"} />}
                  isSelected={field.is_multiline}
                  onClick={() => {
                    onUpdate({ is_multiline: !field.is_multiline });
                  }}
                />
              )}
            </>
          )}
          <DropdownMenuItem
            title={t("common:delete")}
            variant={"menuDanger"}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
            onClick={onDelete}
          />
        </DropdownMenu>
      </div>

      <div
        className={
          "absolute -bottom-1 -right-1 h-3 w-3 cursor-nwse-resize rounded-sm border border-current bg-light opacity-0 group-hover:opacity-100"
        }
        onPointerDown={(event) => {
          setIsResizing(true);
          startPointerDrag(event, (deltaX, deltaY) => {
            const width = round2(
              clamp(field.width + deltaX, fieldMinWidth, baseWidth - field.pos_x)
            );
            const height = round2(
              clamp(field.height + deltaY, fieldMinHeight, baseHeight - field.pos_y)
            );
            if (collidesWithOthers({ pos_x: field.pos_x, pos_y: field.pos_y, width, height }))
              return;
            const font_size =
              field.field_type === EsignFieldType.text
                ? field.font_size
                : getFontSizeForHeight(field.field_type, height);
            onUpdate({ width, height, font_size });
          });
        }}
      />
    </div>
  );
}
